import { clsx } from 'clsx';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from '@seekingalpha/samw-utils';
import copyToClipboard from 'copy-to-clipboard';

import { createGiftLink } from 'Services/api/giftLink/createGiftLink';
import { HappyMomentsHandler } from 'Services/HappyMomentsHandler';
import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';
import { SnackBar } from 'Services/SnackBar';
import { openMobileShare } from 'Shared/Share/utils';
import { useInView } from 'Shared/hooks/useInView';
import { generateGiftLink } from 'Shared/articles/utils';
import { isWrapper } from 'Wrapper/criticals';

import { trackGenerateLink, trackModalShown } from './biEvents';
import { MainView } from './MainView';
import { GiftView } from './GiftView';
import * as styles from './GiftAndShareModal.module.scss';

import type { ArticleTypeT } from 'Constants/articleTypes';
import type { GiftItemT } from 'Services/api/giftLink/types';
import type { GiftArticleT } from 'Shared/articles/types';

type PropsT = {
  article: GiftArticleT;
  articleType: ArticleTypeT;
  availableGift: GiftItemT | null;
  availableGiftTotal: number;
  isMpArticle?: boolean;
  ariaDialogLabelId: string;
};

const DEBOUNCE_INTERVAL = 500;

export const GiftAndShareModal: React.FunctionComponent<PropsT> = ({
  article,
  articleType,
  availableGift,
  availableGiftTotal,
  isMpArticle,
  ariaDialogLabelId,
}) => {
  const queryClient = useQueryClient();
  const { userId } = useAuthorizedUser();
  const [isGenerateLinkView, setIsGenerateLinkView] = useState(false);
  const [giftToken, setGiftToken] = useState<string | null>(
    availableGift && availableGift.id,
  );
  const [setRef, isVisible] = useInView<HTMLDivElement>({
    triggerOnce: true,
  });

  const createGiftMutation = useMutation({
    mutationFn: () => {
      HappyMomentsHandler.share();

      const subjectId = articleType === 'news' ? 'MarketCurrent' : 'Article';

      return createGiftLink(article.id, subjectId);
    },
    onSuccess: ([createdGift]) => {
      queryClient.invalidateQueries({ queryKey: ['shared-links', userId] });
      trackGenerateLink(availableGiftTotal);
      setGiftToken(createdGift.id);

      const createdLink = generateGiftLink(article, createdGift.id);
      if (!isWrapper && createdLink) {
        copyToClipboard(createdLink);
        SnackBar.push({ content: 'Gift Link Copied' });
      }
    },
  });

  const goToGenerateLinkView = useCallback(() => {
    setIsGenerateLinkView(true);
  }, []);

  const goToMainView = useCallback(() => {
    setIsGenerateLinkView(false);
  }, []);

  const onMobileShare = useMemo(() => {
    return debounce(
      (url, addSourceParam) => {
        openMobileShare({
          addSourceParam,
          text: article.title,
          url,
        });
      },
      DEBOUNCE_INTERVAL,
      {
        leading: true,
        trailing: false,
      },
    );
  }, [article.title]);

  const giftLink = useMemo(() => {
    return generateGiftLink(article, giftToken);
  }, [giftToken, article]);

  useEffect(() => {
    if (isVisible) {
      trackModalShown();
    }
  }, [isVisible]);

  return (
    <div
      className={clsx(styles.wrapper, 'flex size-full flex-col md:px-18')}
      ref={setRef}
    >
      {isGenerateLinkView ? (
        <GiftView
          ariaDialogLabelId={ariaDialogLabelId}
          availableGiftTotal={availableGiftTotal}
          giftLink={giftLink}
          goToMainView={goToMainView}
          isPending={createGiftMutation.isPending}
          onGenerate={createGiftMutation.mutate}
          onMobileShare={onMobileShare}
        />
      ) : (
        <MainView
          ariaDialogLabelId={ariaDialogLabelId}
          articleLink={article.links.self}
          availableGift={availableGift}
          availableGiftTotal={availableGiftTotal}
          giftLink={giftLink}
          goToGenerateLinkView={goToGenerateLinkView}
          isMpArticle={isMpArticle}
          onMobileShare={onMobileShare}
        />
      )}
    </div>
  );
};
